/**
 * Product Variant API client (Phase 3b Step 7).
 * Uses relative `/api` — Vite proxy injects X-Api-Token server-side.
 */

import type {
  ProductVariant,
  VariantCreateInput,
  VariantStatus,
} from './types'
import type { AssemblyType } from '@/services/boms/types'

export type VariantsApiError = {
  ok: false
  error: { code: string; message: string; status: number }
}

export class VariantsApiClientError extends Error {
  readonly code: string
  readonly status: number

  constructor(code: string, message: string, status: number) {
    super(message)
    this.name = 'VariantsApiClientError'
    this.code = code
    this.status = status
  }
}

type VariantApiDto = {
  id: string
  product_id: string
  variant_code: string
  color_id: string
  wood_type_id: string | null
  veneer_id: string | null
  name: string | null
  status: VariantStatus
  created_by: string | null
  updated_by: string | null
  version: number
  created_at: string
  updated_at: string
}

async function parseJson<T>(res: Response): Promise<T> {
  return (await res.json()) as T
}

function throwIfApiError(res: Response, body: unknown, fallback: string): void {
  if (res.ok) return
  const err = body as VariantsApiError
  throw new VariantsApiClientError(
    err.error?.code ?? 'HTTP_ERROR',
    err.error?.message ?? `${fallback} (${res.status})`,
    err.error?.status ?? res.status,
  )
}

export function variantApiDtoToUi(dto: VariantApiDto): ProductVariant {
  return {
    id: dto.id,
    productId: dto.product_id,
    variantCode: dto.variant_code,
    colorId: dto.color_id,
    woodTypeId: dto.wood_type_id,
    veneerId: dto.veneer_id,
    name: dto.name,
    status: dto.status,
    version: dto.version,
    createdAt: dto.created_at,
    updatedAt: dto.updated_at,
  }
}

export async function listVariantsByProductApi(
  productId: string,
): Promise<ProductVariant[]> {
  const res = await fetch(
    `/api/products/${encodeURIComponent(productId)}/variants`,
  )
  const body = await parseJson<
    { ok: true; variants: VariantApiDto[] } | VariantsApiError
  >(res)
  throwIfApiError(res, body, 'Failed to list variants')
  return (body as { variants: VariantApiDto[] }).variants.map(variantApiDtoToUi)
}

export async function getVariantApi(
  id: string,
): Promise<ProductVariant | null> {
  const res = await fetch(`/api/variants/${encodeURIComponent(id)}`)
  if (res.status === 404) return null
  const body = await parseJson<
    { ok: true; variant: VariantApiDto } | VariantsApiError
  >(res)
  throwIfApiError(res, body, 'Failed to get variant')
  return variantApiDtoToUi((body as { variant: VariantApiDto }).variant)
}

export async function createVariantApi(
  productId: string,
  input: VariantCreateInput,
): Promise<ProductVariant> {
  const payload: Record<string, unknown> = {
    variant_code: input.variantCode,
    color_id: input.colorId,
    name: input.name ?? null,
    wood_type_id: input.woodTypeId ?? null,
    veneer_id: input.veneerId ?? null,
  }
  if (input.status !== undefined) payload.status = input.status

  const res = await fetch(
    `/api/products/${encodeURIComponent(productId)}/variants`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
  )
  const body = await parseJson<
    { ok: true; variant: VariantApiDto } | VariantsApiError
  >(res)
  throwIfApiError(res, body, 'Tạo Variant thất bại')
  return variantApiDtoToUi((body as { variant: VariantApiDto }).variant)
}

export async function archiveVariantApi(
  id: string,
  status: VariantStatus = 'inactive',
): Promise<ProductVariant> {
  const res = await fetch(`/api/variants/${encodeURIComponent(id)}/archive`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  })
  const body = await parseJson<
    { ok: true; variant: VariantApiDto } | VariantsApiError
  >(res)
  throwIfApiError(res, body, 'Ngừng dùng Variant thất bại')
  return variantApiDtoToUi((body as { variant: VariantApiDto }).variant)
}

export type VariantBomMapDto = {
  variant_id: string
  bom_id: string
  assembly_type?: AssemblyType
  bom_family_id?: string
  mapped_at: string
  mapped_by: string | null
}

/** Returns BomDetail-shaped payload, or null when an existing variant has no mapped BOM. */
export async function getVariantBomApi(
  variantId: string,
  assemblyType?: AssemblyType,
): Promise<{ id: string; name: string; version_label: string; status: string; assembly_type?: AssemblyType } | null> {
  const qs = assemblyType ? `?assembly_type=${assemblyType}` : ''
  const res = await fetch(
    `/api/variants/${encodeURIComponent(variantId)}/bom${qs}`,
  )
  if (res.status === 404) return null
  const body = await parseJson<
    { ok: true; bom: { id: string; name: string; version_label: string; status: string; assembly_type?: AssemblyType } | null } | VariantsApiError
  >(res)
  throwIfApiError(res, body, 'Failed to get variant BOM')
  return (body as { bom: { id: string; name: string; version_label: string; status: string; assembly_type?: AssemblyType } | null }).bom
}

export async function putVariantBomApi(
  variantId: string,
  bomId: string,
  assemblyType?: AssemblyType,
): Promise<VariantBomMapDto> {
  const payload: Record<string, unknown> = { bom_id: bomId }
  if (assemblyType) payload.assembly_type = assemblyType

  const res = await fetch(
    `/api/variants/${encodeURIComponent(variantId)}/bom`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
  )
  const body = await parseJson<
    { ok: true; map: VariantBomMapDto } | VariantsApiError
  >(res)
  throwIfApiError(res, body, 'Map Variant→BOM thất bại')
  return (body as { map: VariantBomMapDto }).map
}

export async function deleteVariantBomApi(
  variantId: string,
  assemblyType?: AssemblyType,
): Promise<void> {
  const qs = assemblyType ? `?assembly_type=${assemblyType}` : ''
  const res = await fetch(
    `/api/variants/${encodeURIComponent(variantId)}/bom${qs}`,
    { method: 'DELETE' },
  )
  const body = await parseJson<{ ok: true } | VariantsApiError>(res)
  throwIfApiError(res, body, 'Gỡ map Variant→BOM thất bại')
}
